import { DefaultButton, Dialog, SpinnerSize, Stack } from '@fluentui/react';
import React, { useEffect } from 'react';
import styles from './styles.module.scss';

// assets

import Label from '../../../typography/label';
import Text from '../../../typography/text';
import LoadingComponent from '../../../feedbacks/loading';
import { TFileType } from '../../../../manifests/fileFormats';
import { IDocumentProps, } from '../../../../props/general';
import moment from 'moment';
import DocumentService from '../../../../services/documents/document';
import FileService from '../../../../services/general/file';
import { DocumentTypeProps } from '../uploads/panel';
import DocumentTypes from '../../../../manifests/documentTypes';
import Details from '../../../typography/details';
import DocumentVisibilities from '../../../../manifests/documentVisibilities';
import GeneralService from '../../../../services/general';


export type TTagVariant = 'default' | 'active' | 'success' | 'light' | 'error' | 'warning' | 'transparent';


const base64ToBlobUrl = (base64Str: string, contentType: string = 'application/pdf'): string => {
    try {
        const byteCharacters = atob(base64Str);
        const byteArrays = [];
        const sliceSize = 512;

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return URL.createObjectURL(blob);
    } catch (e) {
        console.error("Error creating Blob URL for PDF:", e);
        return `data:${contentType};base64,${base64Str}`;
    }
};

type FilePreviewProps = {
    title?: string;
    file: IDocumentProps;
    metadata?: any;
    documentTypes?: DocumentTypeProps[];
    onDismissed(): void;
}

const FilePreview: React.FC<FilePreviewProps> = (props: FilePreviewProps) => {
    const [file, setFile] = React.useState<IDocumentProps>(props.file);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [images, setImages] = React.useState<string[]>([]);
    const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
    const [downloading, setDownloading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        init(props.file);
        return () => {
            if (pdfUrl && pdfUrl.startsWith('blob:')) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [props.file]);

    const init = async (file: IDocumentProps) => {
        try {
            setLoaded(false);
            setError(null);
            setPdfUrl(null);
            setImages([]);

            const base64 = (await DocumentService.get(file.path)).data.data;
            const ext = (file.basename || file.path).split(".").pop()?.toLowerCase() || "";

            if (ext === 'pdf') {
                setPdfUrl(base64ToBlobUrl(base64, 'application/pdf'));
            } else {
                const images = await FileService.getImageBase64(base64, ext);
                setImages(images);
            }
        } catch (err: any) {
            console.error("Failed to load file preview:", err);
            setError(err?.message || "Gagal memuat pratinjau file.");
        } finally {
            setLoaded(true);
        }
    }

    const _onDownload = async () => {
        try {
            setDownloading(true);
            await FileService.download(file);
            setDownloading(false);
        } catch (error) {
            setDownloading(false);
            console.log(error);
        }
    }

    return <Dialog hidden={false}
        dialogContentProps={{ title: props.title || file.basename || file.path.split("/").pop() }}
        onDismiss={props.onDismissed}
        modalProps={{ isBlocking: true }}
        styles={{main: {maxWidth: "1024px !important"}}}>
        <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack grow={1} className={styles.preview}>
                {!loaded ? <LoadingComponent label={'Generating file preview ...'} size={SpinnerSize.medium} /> : null}
                {loaded && error ? <Text style={{ color: '#a80000', fontStyle: 'italic', padding: 20 }}>{error}</Text> : null}
                {loaded && !error && pdfUrl ? (
                    <iframe
                        src={pdfUrl}
                        title={file.basename}
                        style={{ width: '100%', minWidth: '600px', height: '600px', border: 'none', borderRadius: '4px' }}
                    />
                ) : null}
                {loaded && !error && !pdfUrl && images.length > 0 ? images.map((preview, idx) => {
                    return <img key={idx} src={preview} className={styles.image} />;
                }) : null}
                {loaded && !error && !pdfUrl && images.length === 0 ? <Text style={{ fontStyle: 'italic', padding: 20 }}>Pratinjau tidak tersedia untuk format file ini.</Text> : null}
            </Stack>
            <Stack className={styles.properties} tokens={{childrenGap: 15}}>
                <Stack.Item>
                    {!downloading ? <DefaultButton iconProps={{iconName: "Download"}}
                        text={"Download"}
                        label={"Download"}
                        onClick={() => _onDownload()} /> : null}
                    {downloading ? <LoadingComponent spinnerPosition={'baseline'} label={'Downloading ...'} labelPosition={'right'} /> : null}
                </Stack.Item>
                <Stack className={'divider'}></Stack>
                <Details title={'File name'} text={file.basename} />
                <Details title={'Type'} text={(props.documentTypes || DocumentTypes).find((t) => t.key === file.type)?.text || "-"} />
                <Stack className={'divider'}></Stack>
                <Details title={'Size'} text={file.size ? GeneralService.getFriendlyFileSizeText(file.size) : "-"} />
                <Details title={'Visibility'} text={(DocumentVisibilities).find((t) => t.key === file.visibility)?.text || "Private"} />
                <Details title={'Uploaded at'} text={moment(file.uploadedAt).format("DD/MM/YYYY HH:mm")} />
            </Stack>
        </Stack>
    </Dialog>;
};

export default FilePreview;
